import { useContext } from 'react';

import { useSelector } from 'react-redux';

import { useQueries } from '@tanstack/react-query';
import { addYears, endOfMonth, isAfter, startOfMonth } from 'date-fns';

import { useFeature } from '@asteria/component-tools/featureflag';

import * as AccountStore from '@asteria/datalayer/stores/accounts';
import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { ForecasterContext } from '../../context';

export function useHeaderData() {
	const { onSubmit } = useContext(ForecasterContext);

	const hasOverdueFeature = useFeature('forecaster-status-overdue');

	const { dataloader } = useContext(ForecasterContext);

	const scenarioName = useSelector(
		(store) => ScenarioStore.selectors.sourceScenarioData(store)?.name,
	);

	const currentBalance = useSelector(AccountStore.selectors.currentBalance);

	const queries = useQueries({
		queries: [
			{
				queryKey: ['forecaster', 'header', 'history', scenarioName],
				queryFn: async ({ meta }) => {
					const status = ['UNPAID', 'SIGNED'];

					if (hasOverdueFeature) {
						status.push('OVERDUE');
					}

					return onSubmit?.('forecast:fetch:history', {
						startDate: startOfMonth(new Date()).toISOString(),
						endDate: endOfMonth(
							addYears(new Date(), 2),
						).toISOString(),
						scenarioName: scenarioName,
						dataloader: meta?.dataloader,
						status: status,
						fields: `
							date
							types {
								type
								total
								currency
								statuses {
									status
									total
									currency
								}
							}
						`,
					});
				},

				refetchOnMount: true,
				refetchOnReconnect: false,
				refetchOnWindowFocus: false,

				meta: { dataloader },

				select: (response) =>
					(response?.entries ?? [])
						.filter(({ status }) => status !== 'PAID')
						.map(({ date, types }) => ({
							date: date,
							total: types.reduce((acc, { total, type }) => {
								if (type === 'WITHDRAW') {
									return acc - total;
								}

								return acc + total;
							}, 0),
						})),
			},
			{
				queryKey: ['forecaster', 'header', 'forecast', scenarioName],
				queryFn: async ({ meta }) => {
					return onSubmit?.('forecast:fetch:current', {
						scenarioName: scenarioName,
						dataloader: meta?.dataloader,
						fields: `
							nodes {
								date
								types {
									type
									total
									probability
									currency
									categories {
										_id
										details {
											name
											color
										}
										total
										probability
										currency
										tags {
											_id
											details {
												name
												color
											}
										total
										probability
										currency
									}
								}
							}
						}
					`,
					});
				},

				refetchOnMount: true,
				refetchOnReconnect: false,
				refetchOnWindowFocus: false,

				meta: { dataloader },

				select: (response) =>
					(response?.entries ?? []).map(({ date, types }) => ({
						date: date,
						total: types.reduce((acc, { total, type }) => {
							if (type === 'WITHDRAW') {
								return acc - total;
							}

							return acc + total;
						}, 0),
					})),
			},
		],
	});

	const [history, forecast] = queries;

	return {
		loading: queries.some(({ isFetching }) => isFetching),
		data: []
			.concat(history.data ?? [])
			.concat(forecast.data ?? [])
			.reduce(
				(acc, { total, date }) => {
					acc.total += total;

					if (
						!acc.endDate ||
						isAfter(new Date(date), new Date(acc.endDate))
					) {
						acc.endDate = date;
					}

					return acc;
				},
				{ total: currentBalance, endDate: null },
			),
	};
}
